import React, { useState } from 'react';

import type { PessoaMencionada } from '../../api/comentariosApi';
import useUser from '../../hooks/user';
import { notify } from '../../shared/feedback/notify';
import CampoComMencoes, { MencaoEscolhida, idsMencionadosNoTexto } from './CampoComMencoes';
import TextoComMencoes from './TextoComMencoes';

export interface ItemEditavel {
  id: string;
  texto: string;
  autorId: string | null;
  mencionados?: PessoaMencionada[];
  editadoEm?: string | null;
  editavelAte?: string;
}

interface TextoEditavelProps {
  item: ItemEditavel;
  /** A thread já tem resposta: o comentário raiz deixa de ser editável. */
  temResposta: boolean;
  onSalvar: (texto: string, mencionados: string[]) => Promise<void>;
}

const formatarDataHora = (valor: string): string =>
  new Date(valor).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * O texto de um comentário ou resposta, com a correção pelo autor (at-140, ADR 0068).
 *
 * Compartilhado pelos comentários do veículo e pelos de pedido/requisição: a regra é a mesma nos
 * dois, e duas cópias fariam um oferecer o botão quando o outro não.
 *
 * ⚠️ O botão some depois de 10 minutos e quando há resposta, mas quem DECIDE é o servidor: a tela
 * pode estar aberta há mais tempo que isso, e o 409 explica o motivo.
 */
const TextoEditavel: React.FC<TextoEditavelProps> = ({ item, temResposta, onSalvar }) => {
  const usuario = useUser();
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState('');
  const [mencoes, setMencoes] = useState<MencaoEscolhida[]>([]);
  const [salvando, setSalvando] = useState(false);

  const ehAutor = Boolean(usuario?.sub) && item.autorId === usuario?.sub;
  const dentroDoPrazo = item.editavelAte ? Date.now() <= new Date(item.editavelAte).getTime() : false;
  const podeEditar = ehAutor && dentroDoPrazo && !temResposta;

  const abrir = () => {
    setTexto(item.texto);
    setMencoes(
      (item.mencionados ?? [])
        .filter((p) => p.username)
        .map((p) => ({ id: p.id, username: p.username as string })),
    );
    setEditando(true);
  };

  const salvar = async () => {
    const limpo = texto.trim();
    if (!limpo || salvando) return;
    setSalvando(true);
    try {
      await onSalvar(limpo, idsMencionadosNoTexto(limpo, mencoes));
      setEditando(false);
      notify.success('Comentário corrigido.');
    } catch (error) {
      // Falso sucesso não: o texto continua no campo, e o motivo do servidor explica o que impediu
      // (fora dos 10 minutos, já respondido).
      notify.errorFrom(error, 'Não foi possível corrigir o comentário. Tente novamente; se persistir, acione a TI.');
    } finally {
      setSalvando(false);
    }
  };

  if (editando) {
    return (
      <div className="comentarios-veiculo-form-resposta">
        <CampoComMencoes
          placeholder="Corrija o texto (use @ para mencionar alguém)"
          value={texto}
          onChange={setTexto}
          mencoes={mencoes}
          onMencoesChange={setMencoes}
          ariaLabel="Corrigir comentário"
        />
        <div className="comentarios-veiculo-acoes">
          <button
            type="button"
            className="comentarios-veiculo-btn"
            disabled={!texto.trim() || salvando}
            onClick={() => void salvar()}
          >
            {salvando ? 'Salvando...' : 'Salvar correção'}
          </button>
          <button type="button" className="comentarios-veiculo-btn-secundario" onClick={() => setEditando(false)}>
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <p className="comentarios-veiculo-texto">
        <TextoComMencoes texto={item.texto} mencionados={item.mencionados} />
        {item.editadoEm && (
          <span className="comentarios-veiculo-editado" title={`Corrigido pelo autor em ${formatarDataHora(item.editadoEm)}`}>
            {' '}(editado)
          </span>
        )}
      </p>
      {podeEditar && (
        <button type="button" className="comentarios-veiculo-link" onClick={abrir}>
          Editar
        </button>
      )}
    </>
  );
};

export default TextoEditavel;
